// Definir contraseña — página PÚBLICA (fuera de ProtectedRoute).
//
// Punto de aterrizaje del enlace de invitación / recuperación que GoTrue manda
// por email con `redirect_to: .../set-password`. supabase-js (con
// detectSessionInUrl: true) auto-establece la sesión desde el hash de la URL y
// dispara onAuthStateChange (SIGNED_IN para invite, PASSWORD_RECOVERY para
// recovery). Por eso NO va detrás de ProtectedRoute: la sesión puede tener
// contexto de tenant incompleto, y el guard la rebotaría.
//
// Detección de sesión:
//   1. En mount: supabase.auth.getSession() — si el link ya estableció sesión,
//      mostramos el form.
//   2. Además escuchamos onAuthStateChange por si el evento (PASSWORD_RECOVERY /
//      SIGNED_IN) llega DESPUÉS del mount (timing del parseo del hash).
//   Cualquier sesión presente => permitimos definir la contraseña.
//
// Estilo: mismo layout editorial 2 columnas que LoginPage (wordmark serif,
// kanji watermark), reutilizando Input/Button/Label.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Spinner } from '../../components/ui/spinner';
import { useAuth, getHomeForRole } from '../../lib/auth';
import { supabase } from '../../lib/supabase';

type Phase = 'checking' | 'ready' | 'no_session';

const MIN_LEN = 8;

export function SetPasswordPage() {
  const navigate = useNavigate();
  const { state } = useAuth();
  const [phase, setPhase] = useState<Phase>('checking');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setPhase(data.session ? 'ready' : 'no_session');
    });

    // El evento puede dispararse después del mount mientras supabase-js
    // termina de parsear el hash de la URL. Cualquier sesión => mostramos form.
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      if (session) setPhase('ready');
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_LEN) {
      setError(`La contraseña debe tener al menos ${MIN_LEN} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setSubmitting(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setSubmitting(false);

    if (updateError) {
      setError(updateError.message);
      return;
    }

    toast.success('Contraseña definida');
    // Si los claims ya están listos, mandamos al home del rol. Si no, vamos a
    // "/" y dejamos que HomePage rutee cuando el JWT termine de hidratarse.
    // Rol líder también va a "/": puede ser personal DEDICADO a mantenimiento
    // (regla useHomePath, necesita /api/me) y HomePage resuelve su home real.
    const home =
      state.kind === 'signed_in' && state.claims.rol !== 'lider'
        ? getHomeForRole(state.claims.rol)
        : '/';
    navigate(home, { replace: true });
  }

  return (
    <main className="flex min-h-screen flex-col md:flex-row bg-surface-base">
      <EditorialSide />

      <section className="flex flex-1 items-center justify-center bg-surface-elevated px-6 py-10 md:px-16">
        <div className="flex w-full max-w-sm flex-col">
          {phase === 'checking' && (
            <div className="flex min-h-[40vh] items-center justify-center">
              <Spinner />
            </div>
          )}

          {phase === 'no_session' && (
            <>
              <h1 className="text-[22px] font-medium leading-tight tracking-tight text-fg">
                Enlace no válido
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
                Este enlace no es válido o expiró. Pídale a su administrador que le reenvíe la
                invitación, o restablezca su contraseña desde el inicio de sesión.
              </p>
              <Button
                size="lg"
                className="mt-6 h-12 rounded-lg"
                onClick={() => navigate('/login', { replace: true })}
              >
                Ir al inicio de sesión
              </Button>
            </>
          )}

          {phase === 'ready' && (
            <>
              <h1 className="text-[22px] font-medium leading-tight tracking-tight text-fg">
                Defina su contraseña
              </h1>
              <p className="mt-2 text-sm leading-relaxed text-fg-secondary">
                Elija una contraseña para acceder a su cuenta. Mínimo {MIN_LEN} caracteres.
              </p>

              <form onSubmit={onSubmit} className="mt-6 flex flex-col" noValidate>
                <div className="mb-3.5 flex flex-col gap-1.5">
                  <Label htmlFor="password">Nueva contraseña</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="h-11"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>

                <div className="mb-3.5 flex flex-col gap-1.5">
                  <Label htmlFor="confirm">Confirmar contraseña</Label>
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className="h-11"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                  />
                </div>

                {error ? (
                  <p
                    role="alert"
                    className="mb-3 rounded border border-crit/30 bg-crit-soft px-3 py-2 text-xs text-crit"
                  >
                    {error}
                  </p>
                ) : null}

                <Button
                  type="submit"
                  size="lg"
                  loading={submitting}
                  className="mt-2 h-12 rounded-lg"
                >
                  {submitting ? 'Guardando…' : 'Definir contraseña'}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>
    </main>
  );
}

// =============================================================================
// EditorialSide — panel de marca (idéntico patrón a LoginPage, versión compacta)
// =============================================================================

function EditorialSide() {
  return (
    <aside className="relative flex flex-shrink-0 flex-col justify-between overflow-hidden border-b border-border bg-surface-base px-7 py-8 md:w-5/12 md:border-r md:border-b-0 md:px-14 md:py-14">
      <span
        aria-hidden
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 select-none text-fg-disabled opacity-[0.07] md:top-1/2 md:-translate-y-1/2"
        style={{
          fontFamily: "'Hiragino Mincho ProN', 'Yu Mincho', serif",
          fontSize: 'clamp(180px, 30vw, 320px)',
          lineHeight: 0.85,
          letterSpacing: '-10px',
          top: '-40px',
        }}
      >
        自働
      </span>

      <div className="relative z-10 hidden md:block" />

      <div className="relative z-10">
        <h2 className="font-serif text-4xl leading-none tracking-tight text-fg md:text-[56px]">
          Jidokap<span className="text-dot">.</span>
        </h2>
        <p className="mt-2 text-xs leading-snug text-fg-secondary md:mt-4 md:max-w-xs md:text-[13px] md:leading-relaxed">
          Operaciones de manufactura con criterio humano.
        </p>
      </div>

      <div className="relative z-10 hidden md:block" />
    </aside>
  );
}
